import React, { useRef, useContext, useState } from 'react';

import { ReactComponent as DocumentIcon } from '../../assets/icons/document.svg';
import { ReactComponent as FileUploadIcon } from '../../assets/icons/file-input.svg';
import { ReactComponent as DocumentIconDark } from '../../assets/icons/iconsDark/document-dark.svg';
import { ReactComponent as RemoveFileIcon } from '../../assets/icons/remove-icon.svg';
import ThemeContext from '../../contexts/theme-context/ThemeContext';
import { MultiMediaData } from '../../interface/station-management.interface';

interface CustomInputProperties {
  inputLabel?: string;
  acceptedFileType: string;
  placeholder: string;
  selectedFiles: File[] | null;
  name: string;
  onChangeValue: (name: string, file: File[]) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  unselectHandler?: (
    name: string,
    url: string,
    indexToRemove: number,
    type: 'newFile' | 'existingFile',
  ) => void;
  existingName?: string;
  existingFiles?: MultiMediaData[] | null;
  error?: boolean;
  errorMessage?: string;
}

/**
 * File upload input box
 */
function CustomFileInput({
  inputLabel,
  acceptedFileType,
  name,
  placeholder,
  selectedFiles,
  onChangeValue,
  valueRequired,
  readOnly,
  unselectHandler,
  existingName,
  existingFiles,
  error,
  errorMessage,
}: CustomInputProperties) {
  const inputReference = useRef<HTMLInputElement | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const { theme } = useContext(ThemeContext);
  /**
   * Function to handle select file
   */
  const handleSelectFile = (selectedFileList: Array<File>) => {
    if (selectedFileList?.length) {
      onChangeValue(name, selectedFileList);
    }
    setFileInputKey((previous ) => previous  + 1);
  };

  /**
   * Function to handle file unselect
   * @param indexToRemove
   */
  const handleRemoveSelectedFile = (
    url: string,
    indexToRemove: number,
    type: 'newFile' | 'existingFile',
  ) => {
    if (readOnly) {
      return;
    }
    if (unselectHandler && type === 'newFile') {
      unselectHandler(name, url, indexToRemove, type);
    } else if (unselectHandler && type === 'existingFile' && existingName) {
      unselectHandler(existingName, url, indexToRemove, type);
    } else {
      const afterRemovedSelectedFile =
        selectedFiles?.filter((_, index: number) => index !== indexToRemove) || [];
      onChangeValue(name, afterRemovedSelectedFile);
    }
  };

  return (
    <div className="custom-file-input-wrap">
      {inputLabel && (
        <p className={`input-label ${error && 'error'}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className={`custom-file-input-container ${error && 'error'}`}>
        <div className="selected-file-wrap">
          {existingFiles?.length
            ? existingFiles.map((existingFile, index) => (
                <div className="single-selected-file">
                  <span className="doc-icon">
                    {theme === 'light' ? <DocumentIcon /> : <DocumentIconDark />}
                  </span>
                  <p
                    role="presentation"
                    onClick={() => {
                      if (existingFile?.signedUrl) {
                        window.open(existingFile.signedUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}>
                    {existingFile?.fileName}
                  </p>
                  <span
                    className={`remove-icon  ${readOnly ? 'disabled' : ''}`}
                    role="presentation"
                    onClick={() =>
                      handleRemoveSelectedFile(existingFile.url, index, 'existingFile')
                    }>
                    <RemoveFileIcon />
                  </span>
                </div>
              ))
            : null}
          {(() => {
            let selectedFileView;
            if (selectedFiles?.length) {
              selectedFileView = selectedFiles.map((selectedFile, index) => (
                <div className="single-selected-file">
                  <span className="doc-icon">
                    {theme === 'light' ? <DocumentIcon /> : <DocumentIconDark />}
                  </span>
                  <p
                    role="presentation"
                    onClick={() => {
                      if (selectedFile) {
                        window.open(
                          URL.createObjectURL(selectedFile),
                          '_blank',
                          'noopener,noreferrer',
                        );
                      }
                    }}>
                    {selectedFile?.name}
                  </p>
                  <span
                    className={`remove-icon  ${readOnly ? 'disabled' : ''}`}
                    role="presentation"
                    onClick={() => handleRemoveSelectedFile('', index, 'newFile')}>
                    <RemoveFileIcon />
                  </span>
                </div>
              ));
            } else if (
              (selectedFiles?.length === 0 || !selectedFiles) &&
              (existingFiles?.length === 0 || !existingFiles)
            ) {
              selectedFileView = <p className="placeholder">{placeholder}</p>;
            }
            return selectedFileView;
          })()}
        </div>
        <input
          key={fileInputKey}
          ref={inputReference}
          type="file"
          accept={acceptedFileType}
          multiple
          style={{ display: 'none' } as React.CSSProperties}
          onChange={(event) => {
            const files = event?.target?.files;
            if (files) {
              handleSelectFile(Array.from(files));
            }
          }}
        />
        <div className="file-input-icon-wrap">
          <span
            className={`file-input-icon ${readOnly ? 'disabled' : ''} `}
            role="presentation"
            onClick={() => {
              if (!readOnly) {
                inputReference.current?.click();
              }
            }}>
            <FileUploadIcon />
          </span>
        </div>
      </div>
      {error && <p className="custom-input-error-message">{errorMessage}</p>}
    </div>
  );
}

CustomFileInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  unselectHandler: null,
  existingName: '',
  existingFiles: [],
  error: false,
  errorMessage: '',
};

export default CustomFileInput;
